import { Modal } from 'antd';
import queryString from 'query-string';
import { pathOr } from 'ramda';
import { eventChannel } from 'redux-saga';
import {
  take,
  put,
  select,
  call,
  fork,
  takeLatest,
  race,
} from 'redux-saga/effects';

import { SHIPPING_TYPE } from 'constants/all';

import { getSelectedOptionsForQuoteItems } from 'modules/digiQuote/digiQuotePage/data/reducer';
import { rehydrateSelectedOptionsFromUrlParams } from 'modules/digiQuote/digiQuotePage/data/sagas';

import addressService from 'services/addressService';
import paymentService from 'services/paymentService';
import quoteService from 'services/quoteService';

import history from 'utils/browserHistory';

import * as actions from './actions';
import { SECTION_NAME } from './constants';
import * as selectors from './selectors';
import * as types from './types';

const confirm = Modal.confirm;

export default function* root() {
  yield fork(watchCheckout);
  yield takeLatest(types.VALIDATE_ADDRESS_FLOW, watchValidateAddressFlow);
  yield takeLatest(types.FETCH_SHIPPING_RATES_REQUEST, watchFetchShippingRates);
}

export function* initCheckoutPage({ location }) {
  function* getDigiQuoteCheckoutData(token) {
    const params = queryString.parse(location.search);
    const urlParams = {
      preview: !!params.preview,
      facilitate: params.facilitate === 'true',
      withConfirmation: params.with_confirmation === 'true',
    };
    const response = yield call(
      quoteService.getByToken,
      token,
      urlParams.preview || urlParams.facilitate,
    );
    const selectedOptions = yield call(
      rehydrateSelectedOptionsFromUrlParams,
      response.quoteItems,
      params,
    );
    const defaultGroup = Object.values(selectedOptions);
    const shippingEstimation = yield call(
      paymentService.getShippingEstimation,
      token,
      {
        default: defaultGroup,
      },
    );
    const contact = response.contact;
    return {
      urlParams,
      shippingTaxRate: response.shippingTaxRate,
      quoteItems: response.quoteItems,
      quoteData: response,
      canEstimateShipping: shippingEstimation.default.canEstimateShipping,
      shipDate: shippingEstimation.default.shipsOn,
      form: {
        initialAddress: {
          attention: `${contact.firstName} ${contact.lastName}`,
          businessName: pathOr('', ['contact', 'account', 'name'], response),
          address: pathOr(null, ['contact', 'address'], response),
        },
        useShippingAddress: false,
      },
    };
  }

  const { token } = queryString.parse(location.search);
  const payload = yield getDigiQuoteCheckoutData(token);
  yield put(
    actions.initCheckout({
      ...payload,
      token,
    }),
  );
}

function* watchCheckout() {
  for (;;) {
    try {
      yield take(types.CHECKOUT_REQUEST);
      if (!(yield select(selectors.getValidationStatus)).isValid) {
        throw new Error('Please fill all required fields.');
      }
      const {
        shippingAddress,
        billingAddress,
        paymentMethod,
        creditCardDetails,
        purchaseOrderDetails,
        useShippingAddress,
        shippingOption,
        shippingRate,
      } = yield select(selectors.getCheckoutData);
      const quoteToken = yield select(selectors.getQuoteToken);
      const quoteItems = yield select(selectors.getQuoteItems);
      const selectedOptionsList = yield select(
        getSelectedOptionsForQuoteItems,
        quoteItems,
      );
      const urlParams = yield select(selectors.getUrlParams);
      const payload = {
        quoteKey: quoteToken,
        selectedOptionsList,
        shippingInfo: shippingAddress,
        billingInfo: useShippingAddress ? shippingAddress : billingAddress,
        paymentMethod,
        ...(creditCardDetails || {}),
        ...(purchaseOrderDetails || {}),
        shippingOption: {
          ...shippingOption,
          method:
            shippingRate &&
            shippingOption.type == SHIPPING_TYPE.SUPPLIER_ACCOUNT
              ? shippingRate.method
              : shippingOption.method,
        },
        shippingRateToken: shippingRate && shippingRate.token,
        facilitate: !!urlParams.facilitate,
        withConfirmation: !!urlParams.withConfirmation,
      };
      yield call(paymentService.checkout, payload);
      yield put(actions.checkout.success());
      history.push(`/success/${quoteToken}`);
    } catch (error) {
      yield put(actions.checkout.error(error));
    }
  }
}

function* watchValidateAddressFlow(action) {
  try {
    const { address, onEdit } = action.payload;
    yield put(actions.validateAddress.request());
    const response = yield call(addressService.validateInformation, address);
    yield put(actions.validateAddress.success(response));
    if (response.isValid) {
      if (response.suggestedAddresses.length === 0) {
        yield put(actions.validateAddress.confirm(address));
        yield confirmShippingAddress(address);
      } else {
        yield put(actions.validateAddress.pickFromSuggested(response));
        const winner = yield race({
          confirm: take(types.VALIDATE_ADDRESS_CONFIRM),
          edit: take(types.VALIDATE_ADDRESS_EDIT),
        });
        if (winner.confirm) {
          yield confirmShippingAddress(winner.confirm.payload.address);
        } else {
          onEdit(winner.edit.payload.address);
        }
      }
    } else {
      if (yield confirmProceedingWithInvalidShippingAddress()) {
        yield put(actions.validateAddress.confirm(address));
        yield confirmShippingAddress(address);
      } else {
        onEdit(address);
      }
    }
  } catch (error) {
    yield put(actions.validateAddress.error(error));
  }
}

function* confirmProceedingWithInvalidShippingAddress() {
  const channel = eventChannel((emitter) => {
    const modal = confirm({
      autoFocusButton: 'ok',
      title: "We couldn't validate your address",
      content: 'An invalid shipping address can cause delivery delays.',
      okText: 'Proceed anyway',
      cancelText: 'Back',
      onOk() {
        emitter(true);
      },
      onCancel() {
        emitter(false);
      },
    });
    return () => modal.destroy();
  });
  return yield take(channel);
}

function* confirmShippingAddress(address) {
  yield put(
    actions.confirmCheckoutData({
      data: { shippingAddress: address },
      section: SECTION_NAME.SHIPPING_INFO,
      nextSection: yield select(
        selectors.getNextSection,
        SECTION_NAME.SHIPPING_INFO,
      ),
    }),
  );
}

function* watchFetchShippingRates(action) {
  let e;
  try {
    const quoteToken = yield select(selectors.getQuoteToken);
    const shippingAddress = yield select(selectors.getConfirmedShippingAddress);
    const quoteItems = yield select(selectors.getQuoteItems);
    const selectedOptionsList = yield select(
      getSelectedOptionsForQuoteItems,
      quoteItems,
    );
    const response = yield call(paymentService.getShippingRates, quoteToken, {
      address: (shippingAddress || {}).address,
      itemGroups: {
        default: selectedOptionsList,
      },
    });
    yield put(actions.fetchShippingRates.success(response));
  } catch (error) {
    yield put(actions.fetchShippingRates.error((e = error)));
  }
  action.payload.cb(e);
}
